/**
 * from https://github.com/Interencheres/asgard/blob/master/utils/sanitizeAndExtractHtml.ts
 */
import { Parser } from "htmlparser2";

export type SanitizeAndExtractHtmlOptions = {
    allowedTags?: string[];
    allowedAttributes?: { [tag: string]: string[] };
    allowedIframeHostnames?: string[];
};

export type SanitizeAndExtractHtmlResult = {
    type: string;
    attrs?: { [attribute: string]: string };
    content?: string;
};

type SanitizeAndExtractMatchElement = {
    tag: string;
    content?: string;
    pos: number;
    length?: number;
    attrs: { [attribute: string]: string };
};

const defaultOptions = {
    allowedTags: ["iframe", "a"],
    allowedAttributes: {
        iframe: ["src", "width", "height", "frameborder", "allow", "allowfullscreen"],
        a: ["href", "target"]
    },
    allowedIframeHostnames: ["www.youtube.com", "player.vimeo.com", "my.matterport.com"]
};

const keepOnlyWantedAttrs = (attrs: { [attribute: string]: string }, allowedAttributes: string[]) => {
    for (const attr of Object.keys(attrs)) {
        if (!allowedAttributes?.includes(attr)) {
            delete attrs[attr];
        }
    }
};

const sanitizeAndExtractHtml = (
    html: string,
    options: SanitizeAndExtractHtmlOptions = defaultOptions
): SanitizeAndExtractHtmlResult[] => {
    const matches: SanitizeAndExtractMatchElement[] = [];
    const results: SanitizeAndExtractHtmlResult[] = [];
    let current: SanitizeAndExtractMatchElement = null;

    const parser = new Parser({
        onopentag(tag: string, attrs: { [p: string]: string }) {
            if (
                !options.allowedTags.includes(tag) ||
                (tag === "iframe" &&
                    options.allowedIframeHostnames?.length &&
                    (!attrs.src || !options.allowedIframeHostnames.includes(new URL(attrs.src)?.hostname)))
            ) {
                return;
            }
            keepOnlyWantedAttrs(attrs, options.allowedAttributes?.[tag]);
            current = { tag, pos: parser.startIndex, attrs, content: "" };
        },
        onclosetag(tag: string) {
            if (current && tag === current.tag) {
                current.length = parser.endIndex - current.pos + 1;
                matches.push(current);
                current = null;
            }
        },
        ontext(content: string) {
            if (current) {
                current.content = content;
            }
        }
    });
    parser.parseComplete(html);

    if (!matches.length) {
        return [{ type: "text", content: html }];
    }

    let idx = 0;
    for (const { tag, content, attrs, pos, length } of matches) {
        if (pos > idx) {
            results.push({ type: "text", content: html.substr(idx, pos - idx) });
        }
        results.push({ type: tag, content, attrs });
        idx = pos + length;
    }
    if (idx < html.length - 1) {
        results.push({ type: "text", content: html.substr(idx) });
    }

    return results;
};

export default sanitizeAndExtractHtml;
